// Report a Concern Modal Component
import type { ConcernCategory } from '../types';
import { submitConcernReport } from '../services/reportService';
import { showToast } from '../components/Toast';

export interface ReportConcernModalProps {
  type: 'user' | 'event';
  targetId: string;
  targetNameOrTitle: string;
}

export function renderReportConcernModal(props: ReportConcernModalProps): string {
  const isEvent = props.type === 'event';

  return `
    <div id="modal-report-concern" class="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in font-sans">
      <div class="card-workshop max-w-lg w-full p-6 sm:p-7 relative border-amber-500/40 bg-[#090b12] space-y-5 shadow-[0_0_25px_rgba(255,183,3,0.15)]">
        <div class="laser-line-amber absolute top-0 left-0 right-0"></div>

        <!-- Header -->
        <div class="flex items-start justify-between gap-3 border-b border-white/[0.08] pb-3">
          <div>
            <div class="flex items-center gap-2 mb-1">
              <span class="px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-transparent text-amber-400 border border-amber-400/50 flex items-center gap-1.5 shadow-[0_0_8px_rgba(255,183,3,0.2)]">
                <i class="fa-solid fa-flag"></i> Trust & Safety
              </span>
            </div>
            <h2 class="text-lg font-black text-slate-100">Report a Concern</h2>
            <p class="text-xs text-slate-400 mt-0.5">
              Flagging ${isEvent ? 'event' : 'member'}: <strong class="text-slate-200">"${props.targetNameOrTitle}"</strong>
            </p>
          </div>
          <button id="close-report-modal-btn" class="text-slate-400 hover:text-white p-1 text-base transition-colors cursor-pointer">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Enforcement Notice -->
        <div class="p-3 rounded-xl bg-amber-950/30 border border-amber-500/40 text-[11px] text-amber-200/90 leading-relaxed flex items-start gap-2.5">
          <i class="fa-solid fa-shield-halved text-amber-400 text-sm mt-0.5 flex-shrink-0"></i>
          <div>
            ${isEvent 
              ? 'Submitting a report will <strong>immediately unlist this event</strong> and mark it "Under Review" for its creator while admins investigate.' 
              : 'Submitting a report will <strong>immediately suspend this user\'s account</strong> under review while admins investigate.'}
          </div>
        </div>

        <form id="report-concern-form" class="space-y-4">
          
          <!-- Category Radio Selection -->
          <div class="space-y-2">
            <label class="block text-xs font-bold uppercase tracking-wider text-slate-300">
              What is your concern? <span class="text-amber-400">*</span>
            </label>

            <div class="space-y-2 text-xs">
              
              <label class="flex items-start gap-3 p-3 rounded-xl bg-transparent border border-white/[0.08] hover:border-amber-400/40 cursor-pointer transition-colors">
                <input type="radio" name="concern-category" value="inappropriate" class="mt-0.5 text-amber-400 focus:ring-0" required checked>
                <div>
                  <div class="font-bold text-slate-200">(a) Inappropriate</div>
                  <div class="text-[11px] text-slate-400">Offensive language, explicit behavior, harassment, or unsuitable for family spaces.</div>
                </div>
              </label>

              <label class="flex items-start gap-3 p-3 rounded-xl bg-transparent border border-white/[0.08] hover:border-amber-400/40 cursor-pointer transition-colors">
                <input type="radio" name="concern-category" value="unsafe" class="mt-0.5 text-amber-400 focus:ring-0">
                <div>
                  <div class="font-bold text-slate-200">(b) Unsafe</div>
                  <div class="text-[11px] text-slate-400">Dangerous work conditions, lack of safety/PPE, reckless conduct, or physical hazard.</div>
                </div>
              </label>

              <label class="flex items-start gap-3 p-3 rounded-xl bg-transparent border border-white/[0.08] hover:border-amber-400/40 cursor-pointer transition-colors">
                <input type="radio" name="concern-category" value="spam" class="mt-0.5 text-amber-400 focus:ring-0">
                <div>
                  <div class="font-bold text-slate-200">(c) Spam / Phishing</div>
                  <div class="text-[11px] text-slate-400">Commercial ads, deceptive schemes, unwanted solicitation, or malicious links.</div>
                </div>
              </label>

              <label class="flex items-start gap-3 p-3 rounded-xl bg-transparent border border-white/[0.08] hover:border-amber-400/40 cursor-pointer transition-colors">
                <input type="radio" name="concern-category" value="other" class="mt-0.5 text-amber-400 focus:ring-0">
                <div>
                  <div class="font-bold text-slate-200">(d) Other</div>
                  <div class="text-[11px] text-slate-400">Any other violation of Pops Culture trust and community standards.</div>
                </div>
              </label>

            </div>
          </div>

          <!-- Optional Message -->
          <div class="space-y-1.5">
            <label class="block text-xs font-bold uppercase tracking-wider text-slate-300">
              Additional Details <span class="text-slate-500 font-normal">(Optional)</span>
            </label>
            <textarea 
              id="report-message"
              rows="3" 
              maxlength="1000"
              placeholder="Provide any context, observations, or specifics that will assist the moderation team..."
              class="w-full bg-[#0a0c13] border border-white/[0.12] rounded-xl px-3.5 py-2.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 transition-all resize-none"
            ></textarea>
          </div>

          <!-- Buttons -->
          <div class="pt-3 border-t border-white/[0.08] flex items-center justify-between gap-3">
            <button type="button" id="cancel-report-btn" class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white border border-transparent hover:border-white/10 transition-colors cursor-pointer">
              Cancel
            </button>
            <button type="submit" id="submit-report-btn" class="px-5 py-2.5 rounded-xl text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-400 hover:bg-amber-500 hover:text-black transition-all flex items-center gap-1.5 shadow-[0_0_12px_rgba(255,183,3,0.3)] cursor-pointer">
              <i class="fa-solid fa-flag"></i> Submit Concern
            </button>
          </div>

        </form>

      </div>
    </div>
  `;
}

export function bindReportConcernModalEvents(
  props: ReportConcernModalProps,
  onClose: () => void,
  onSubmitted: () => void
): void {
  document.getElementById('close-report-modal-btn')?.addEventListener('click', onClose);
  document.getElementById('cancel-report-btn')?.addEventListener('click', onClose);

  const form = document.getElementById('report-concern-form') as HTMLFormElement;
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();

    const selectedCategory = (form.querySelector('input[name="concern-category"]:checked') as HTMLInputElement)?.value as ConcernCategory || 'other';
    const message = (document.getElementById('report-message') as HTMLTextAreaElement)?.value || '';

    const labels: Record<ConcernCategory, string> = {
      inappropriate: 'Inappropriate Content or Conduct',
      unsafe: 'Unsafe Environment or Tools Practice',
      spam: 'Spam, Phishing, or Commercial Solicitation',
      other: 'Other Community Guidelines Violation'
    };

    const submitBtn = document.getElementById('submit-report-btn') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Submitting...';
    }

    try {
      await submitConcernReport({
        type: props.type,
        targetId: props.targetId,
        targetNameOrTitle: props.targetNameOrTitle,
        category: selectedCategory,
        categoryLabel: labels[selectedCategory],
        message
      });

      showToast(`Concern submitted. This ${props.type} has been placed under review.`, 'warning');
      onSubmitted();
    } catch (err: any) {
      showToast(err.message || 'Failed to submit report', 'error');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<i class="fa-solid fa-flag"></i> Submit Concern';
      }
    }
  });
}
